import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaSync, FaEdit, FaChartPie, FaToggleOn, FaToggleOff, FaBolt, FaSearch, FaWallet } from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

/* ─── animated counter ─────────────────────────────── */
const useCountUp = (target, duration = 1000) => {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    if (target == null) return;
    const from = prev.current;
    const t0 = performance.now();
    const tick = now => {
      const p = Math.min((now - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(from + (target - from) * e);
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = target;
    };
    requestAnimationFrame(tick);
  }, [target]);
  return val;
};

const AnimNum = ({ value, tick: _tick }) => {
  const v = useCountUp(value ?? 0);
  return <>{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)}</>;
};

/* ─── Patlama bildirimi ──────────────────────────────── */
let _notifId = 0;
const NotifManager = (() => {
  let listeners = [];
  return {
    subscribe: (fn) => { listeners.push(fn); return () => { listeners = listeners.filter(l => l !== fn); }; },
    emit: (notif) => listeners.forEach(l => l({ ...notif, id: ++_notifId, ts: Date.now() })),
  };
})();

const KasaNotifOverlay = () => {
  const [notifler, setNotifler]   = React.useState([]);
  const [goruldu, setGoruldu]     = React.useState(false);     // kullanıcı sayfada aktif mi
  const timerRef                  = React.useRef({});           // her notif için ayrı timer
  const gorulduRef                = React.useRef(false);

  // Kullanıcı sayfada hareketlenince "gördü" say, geri sayımı başlat
  React.useEffect(() => {
    const aktifOl = () => {
      if (gorulduRef.current) return;
      gorulduRef.current = true;
      setGoruldu(true);
    };
    window.addEventListener('mousemove', aktifOl, { once: true });
    window.addEventListener('keydown',   aktifOl, { once: true });
    window.addEventListener('click',     aktifOl, { once: true });
    window.addEventListener('scroll',    aktifOl, { once: true });
    // Sekme tekrar aktif olunca da say
    const visChange = () => { if (!document.hidden) aktifOl(); };
    document.addEventListener('visibilitychange', visChange);
    return () => {
      document.removeEventListener('visibilitychange', visChange);
    };
  }, []);

  // Yeni notif gelince kuyruğa ekle
  React.useEffect(() => {
    return NotifManager.subscribe(n => {
      setNotifler(prev => [...prev.slice(-5), { ...n, goruldu: false }]);
    });
  }, []);

  // "Gördü" state'i değişince bekleyen notifleri sırayla dismiss et
  React.useEffect(() => {
    if (!goruldu) return;
    notifler.forEach((n, i) => {
      if (timerRef.current[n.id]) return; // zaten başlatıldı
      const delay = i * 600; // sıralı çıkış
      timerRef.current[n.id] = setTimeout(() => {
        setNotifler(prev => prev.filter(x => x.id !== n.id));
        delete timerRef.current[n.id];
      }, 3000 + delay);
    });
  }, [goruldu, notifler.length]);

  if (!notifler.length) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column', pointerEvents: 'none',
    }}>
      {notifler.map((n, i) => {
        // progress bar animasyonu sadece kullanıcı gördükten sonra başlar
        const progStyle = goruldu ? {
          position: 'absolute', bottom: 0, left: 0,
          height: 3, background: n.renk, opacity: 0.9,
          boxShadow: `0 0 10px ${n.renk}`,
          animation: `notifProgress ${3000 + i * 600}ms linear both`,
          animationDelay: '0ms',
        } : {
          // Görülmeden önce: tam dolu bar, hiç erimesin
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 3, background: n.renk, opacity: 0.9,
          boxShadow: `0 0 10px ${n.renk}`,
        };

        return (
          <div key={n.id} style={{
            position: 'relative',
            display: 'flex', alignItems: 'center',
            background: `linear-gradient(90deg, rgba(8,9,12,0.98) 0%, ${n.renk}40 40%, ${n.renk}40 60%, rgba(8,9,12,0.98) 100%)`,
            borderBottom: `2px solid ${n.renk}90`,
            padding: '10px 28px',
            animation: 'notifSlideDown 0.3s cubic-bezier(.22,1,.36,1) both',
            animationDelay: `${i * 40}ms`,
            justifyContent: 'center',
            gap: 20,
            boxShadow: `0 4px 32px ${n.renk}30, inset 0 1px 0 ${n.renk}20`,
          }}>
            {/* Sol: kasa renk bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 4, height: 28, borderRadius: 2, background: n.renk, flexShrink: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: n.renk, display: 'inline-block' }} />
                <span style={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{n.kasa}</span>
              </div>
            </div>

            {/* Orta: miktar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, borderRadius: 6,
                background: `${n.renk}20`, border: `1px solid ${n.renk}40`,
                color: n.renk, fontSize: '0.75rem', fontWeight: 900,
              }}>
                {n.yon === 'yatirim' ? '↑' : '↓'}
              </span>
              <span style={{
                color: n.renk, fontSize: '1.15rem', fontWeight: 900,
                fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
                textShadow: `0 0 20px ${n.renk}90`,
              }}>
                {n.yon === 'yatirim' ? '+' : '−'}{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(Math.abs(n.fark))} ₺
              </span>
            </div>

            {/* Sağ: etiket */}
            <div style={{
              color: '#334155', fontSize: '0.65rem', fontWeight: 600,
              background: `${n.renk}0a`, border: `1px solid ${n.renk}18`,
              borderRadius: 6, padding: '2px 9px',
            }}>
              {n.yon === 'yatirim' ? 'yeni yatırım' : 'yeni çekim'}
            </div>

            {/* Geri sayım göstergesi */}
            {!goruldu && (
              <div style={{ color: '#1e293b', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: n.renk, opacity: 0.5, animation: 'blink 1.2s ease infinite', display: 'inline-block' }} />
                bekliyor
              </div>
            )}

            {/* Progress bar */}
            <div style={progStyle} />
          </div>
        );
      })}
    </div>
  );
};

/* ─── per-kasa accent colours ───────────────────────── */
const ACCENT = {
  FAYNPAY:  ['#f27127', '#ff6b35'],
  NEOPAYS:  ['#06b6d4', '#0891b2'],
  ULTRAPAY: ['#8b5cf6', '#7c3aed'],
  FULGUR:   ['#f59e0b', '#d97706'],
  ANINDA:   ['#10b981', '#059669'],
  MİNİ:     ['#ec4899', '#db2777'],
  ENHİZLİ:  ['#3b82f6', '#2563eb'],
  DEFAULT:  ['#94a3b8', '#64748b'],
};
const accent = id => ACCENT[id?.toUpperCase()] ?? ACCENT.DEFAULT;

/* ─── KasaYonetimi ──────────────────────────────────── */
const KasaYonetimi = () => {
  const bugun = dayjs();
  const [kasalar, setKasalar]                 = useState([]);
  const [kasaOzet, setKasaOzet]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [canliLoading, setCanliLoading]       = useState(false);
  const [ilkYukleme, setIlkYukleme]           = useState(true);
  const [canliGuncelleme, setCanliGuncelleme] = useState(null);
  const [canliHata, setCanliHata]             = useState(null);
  const [canliPlatform, setCanliPlatform]     = useState(null);
  const [search, setSearch]                   = useState('');
  const [ay, setAy]                           = useState(bugun.month() + 1);
  const [yil, setYil]                         = useState(bugun.year());
  const [editModal, setEditModal]             = useState(null);
  const [ozetModal, setOzetModal]             = useState(null);
  const [ozetData, setOzetData]               = useState(null);
  const [ozetLoading, setOzetLoading]         = useState(false);
  const [form, setForm]                       = useState({ devirKasasi: '', dovizKuru: '', aktif: true });
  const [tick, setTick]                       = useState(0);
  const [hovered, setHovered]                 = useState(null);
  const [gunlukData, setGunlukData]           = useState([]);
  const [flashKasalar, setFlashKasalar]       = useState({});
  const [genelRekor, setGenelRekor]           = useState(null);
  const [countdown, setCountdown]             = useState(20);
  const oncekiOzetRef                         = useRef({});
  const intervalRef                           = useRef(null);
  const countdownRef                          = useRef(null);

  const AY_ADLARI = ['','Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
  const fmt = n => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(n ?? 0);

  const fetchKasalar = async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/kasa/all'); setKasalar(r.data); }
    catch { toast.error('Kasalar yüklenemedi!'); }
    finally { setLoading(false); }
  };

  const fetchCanli = useCallback(async () => {
    setCanliLoading(true); setCanliHata(null);
    try {
      const [r, gR, rR] = await Promise.all([
        axiosInstance.get(`/rapor/canli?ay=${ay}&yil=${yil}`),
        axiosInstance.get(`/rapor/gunluk-yontem?ay=${ay}&yil=${yil}`),
        axiosInstance.get('/rapor/rekor'),
      ]);
      const yeniRapor = r.data.rapor ?? [];

      // Diff — önceki değerlerle karşılaştır, değişimleri bildir
      const onceki = oncekiOzetRef.current;
      if (Object.keys(onceki).length > 0) {
        yeniRapor.forEach(k => {
          const prev = onceki[k.kasa];
          if (!prev) return;
          const yatFark = (k.liveYatirim ?? 0) - (prev.liveYatirim ?? 0);
          const cekFark = (k.liveCekim   ?? 0) - (prev.liveCekim   ?? 0);
          if (yatFark > 10) {
            NotifManager.emit({ kasa: k.kasa, yon: 'yatirim', fark: yatFark, renk: ACCENT[k.kasa?.toUpperCase()]?.[0] ?? '#4ade80' });
            setFlashKasalar(prev => ({ ...prev, [k.kasa]: { yon: 'yatirim', ts: Date.now() } }));
            setTimeout(() => setFlashKasalar(prev => { const n = { ...prev }; delete n[k.kasa]; return n; }), 1800);
          }
          if (cekFark > 10) {
            NotifManager.emit({ kasa: k.kasa, yon: 'cekim', fark: cekFark, renk: '#f87171' });
            setFlashKasalar(prev => ({ ...prev, [k.kasa]: { yon: 'cekim', ts: Date.now() } }));
            setTimeout(() => setFlashKasalar(prev => { const n = { ...prev }; delete n[k.kasa]; return n; }), 1800);
          }
        });
      }
      // Yeni snapshot'ı kaydet
      oncekiOzetRef.current = Object.fromEntries(yeniRapor.map(k => [k.kasa, k]));

      setKasaOzet(yeniRapor);
      setCanliGuncelleme(r.data.guncelleme);
      setCanliPlatform(r.data.platform ?? null);
      setGunlukData(gR.data ?? []);
      setGenelRekor(rR.data ?? null);
      setTick(t => t + 1);
      setIlkYukleme(false);
    } catch {
      setCanliHata('Platform bağlantısı kurulamadı');
      try { const fb = await axiosInstance.get(`/rapor/kasalar?ay=${ay}&yil=${yil}`); setKasaOzet(fb.data); } catch {}
    } finally { setCanliLoading(false); }
  }, [ay, yil]);

  useEffect(() => { fetchKasalar(); }, []);

  useEffect(() => {
    fetchCanli();
    setCountdown(20);

    // Countdown timer
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) return 20;
        return prev - 1;
      });
    }, 1000);

    // 20 saniyede bir yenile
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      fetchCanli();
      setCountdown(20);
    }, 20000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [ay, yil]);

  const ozetMap = {};
  kasaOzet.forEach(k => { ozetMap[k.kasa] = k; });

  const filtered = kasalar
    .filter(k => k._id !== 'DIS_KASA')
    .filter(k => k._id.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aVal = ozetMap[a._id]?.canliKasa ?? ozetMap[a._id]?.guncelKasa ?? 0;
      const bVal = ozetMap[b._id]?.canliKasa ?? ozetMap[b._id]?.guncelKasa ?? 0;
      return bVal - aVal;
    });

  const handleOzetAc = async (kasa) => {
    setOzetModal(kasa); setOzetLoading(true); setOzetData(null);
    try { const r = await axiosInstance.get(`/kasa/${kasa._id}/ozet?ay=${ay}&yil=${yil}`); setOzetData(r.data); }
    catch { toast.error('Özet yüklenemedi!'); }
    finally { setOzetLoading(false); }
  };

  const handleEditAc = (kasa) => {
    setEditModal(kasa);
    setForm({ devirKasasi: kasa.devirKasasi ?? '', dovizKuru: kasa.dovizKuru ?? '', aktif: kasa.aktif });
  };

  const handleGuncelle = async () => {
    try {
      await axiosInstance.patch(`/kasa/update/${editModal._id}`, form);
      toast.success('Güncellendi.'); setEditModal(null);
      fetchKasalar(); fetchCanli();
    } catch { toast.error('Güncelleme başarısız!'); }
  };

  const liveStatus = canliHata ? 'error' : canliLoading ? 'loading' : canliGuncelleme ? 'live' : 'idle';
  const statusColors = { error: '#ef4444', loading: '#f59e0b', live: '#22c55e', idle: '#475569' };
  const statusLabels = { error: 'OFFLİNE', loading: 'SYNC...', live: 'CANLI', idle: '—' };

  return (
    <div style={V.page}>
      {/* ambient glow blobs */}
      <div style={{ ...V.blob, top: -120, left: -80,  background: 'radial-gradient(circle, rgba(242,113,39,0.07) 0%, transparent 70%)' }} />
      <div style={{ ...V.blob, bottom: -80, right: -60, background: 'radial-gradient(circle, rgba(6,182,212,0.05) 0%, transparent 70%)' }} />

      {/* ── HEADER ─────────────────────────────────── */}
      <div style={V.header}>
        <div>
          <h2 style={V.headerTitle}>
            <FaWallet style={{ color: '#f27127', marginRight: 10 }} />
            Kasa Takip
          </h2>
          <p style={V.headerSub}>{AY_ADLARI[ay]} {yil} • Anlık Platform Verisi</p>
          <div style={V.eyebrow}>
            <span style={{ ...V.statusDot, background: statusColors[liveStatus], boxShadow: liveStatus === 'live' ? `0 0 8px ${statusColors.live}` : 'none', animation: liveStatus !== 'error' ? 'blink 2s ease infinite' : 'none' }} />
            <span style={{ color: statusColors[liveStatus], fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em' }}>
              {statusLabels[liveStatus]}
            </span>
            {canliGuncelleme && liveStatus === 'live' && (
              <span style={{ color: '#475569', fontSize: '0.68rem' }}>
                · {new Date(canliGuncelleme).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </div>

        </div>
        <div style={V.controls}>
          <div style={V.searchWrap}>
            <FaSearch style={{ color: '#475569', fontSize: '0.7rem' }} />
            <input style={V.searchInput} placeholder="kasa ara..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select style={V.sel} value={ay} onChange={e => setAy(Number(e.target.value))}>
            {AY_ADLARI.slice(1).map((a, i) => <option key={i+1} value={i+1}>{a}</option>)}
          </select>
          <select style={V.sel} value={yil} onChange={e => setYil(Number(e.target.value))}>
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={{ ...V.syncBtn, position: 'relative' }} onClick={() => { fetchCanli(); setCountdown(20); }}>
            <FaSync style={(loading || canliLoading) ? { animation: 'spin 1s linear infinite' } : {}} />
            <span style={{ position: 'absolute', top: -6, right: -6, background: '#0f1115', border: '1px solid #1e2530', borderRadius: 10, color: canliLoading ? '#f27127' : '#475569', fontSize: '0.52rem', fontWeight: 700, padding: '1px 5px', lineHeight: 1.4 }}>
              {canliLoading ? '...' : `${countdown}s`}
            </span>
          </button>
        </div>
      </div>

      {/* ── ANLIK ÖZET KARTLARI ────────────────────── */}
      {canliPlatform && (() => {
        const yat = canliPlatform.toplamYatirim  ?? 0;
        const cek = canliPlatform.toplamCekim    ?? 0;
        const kom = canliPlatform.toplamKomisyon ?? 0;
        const net = yat - cek - kom;
        const max = Math.max(yat, cek, 1);
        const kartlar = [
          { label: 'Anlık Toplam Yatırım',  val: yat, renk: '#4ade80', pct: (yat/max)*100, sym: '↑' },
          { label: 'Anlık Toplam Çekim',    val: cek, renk: '#f87171', pct: (cek/max)*100, sym: '↓' },
          { label: 'Anlık Toplam Komisyon', val: kom, renk: '#fbbf24', pct: Math.min((kom/Math.max(yat,1))*100, 100), sym: '%' },
          { label: 'Anlık Net',             val: net, renk: net >= 0 ? '#4ade80' : '#f87171', pct: Math.min(Math.abs(net)/Math.max(yat,1)*100, 100), sym: net >= 0 ? '+' : '−' },
        ];
        return (
          <div style={V.ozKartGrid}>
            {kartlar.map(({ label, val, renk, pct, sym }, i) => (
              <div key={i} style={{ ...V.ozKart, animationDelay: `${i * 55}ms` }}>
                {/* Büyük arka plan glow */}
                <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 10% 50%, ${renk}09 0%, transparent 65%)`, pointerEvents: 'none', borderRadius: 'inherit' }} />
                {/* Sol renk aksanı */}
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg, ${renk}, ${renk}40)`, borderRadius: '14px 0 0 14px' }} />
                <div style={V.ozKartInner}>
                  {/* Sembol kutusu */}
                  <div style={{ ...V.ozKartSymBox, color: renk, background: `${renk}10`, border: `1px solid ${renk}20` }}>
                    <span style={{ fontSize: '1.1rem', fontWeight: 900, lineHeight: 1 }}>{sym}</span>
                  </div>
                  {/* İçerik */}
                  <div style={V.ozKartContent}>
                    <span style={{ ...V.ozKartLabel, color: '#475569' }}>{label}</span>
                    <div style={{ ...V.ozKartVal, color: renk }}>
                      {(canliLoading && ilkYukleme)
                        ? <span style={V.ozKartSkel} />
                        : <><AnimNum value={val} tick={tick} /><span style={V.ozKartTL}> ₺</span></>
                      }
                    </div>
                    {/* Progress bar */}
                    <div style={V.ozKartTrack}>
                      <div style={{ ...V.ozKartFill, width: `${pct.toFixed(1)}%`, background: `linear-gradient(90deg, ${renk}cc, ${renk}55)` }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── REKOR CHALLENGE ──────────────────────── */}
      {canliPlatform && (() => {
        const anlikYat  = canliPlatform.toplamYatirim ?? 0;
        const rekor     = genelRekor?.yatirim ?? 0;
        // "YYYY-MM-DD" formatını timezone kayması olmadan parse et
        const rekorTarih = genelRekor?.tarih
          ? (() => {
              const [yil, ay, gun] = genelRekor.tarih.split('-').map(Number);
              return new Date(yil, ay - 1, gun).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
            })()
          : null;
        const pct      = rekor > 0 ? Math.min((anlikYat / rekor) * 100, 100) : 0;
        const gecti    = anlikYat >= rekor && rekor > 0;
        const fmt2     = n => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

        return (
          <div style={V.rekorCard}>
            {/* arka glow */}
            <div style={{ position: 'absolute', inset: 0, background: gecti ? 'radial-gradient(ellipse at 50% 0%, rgba(251,191,36,0.09) 0%, transparent 65%)' : 'radial-gradient(ellipse at 0% 50%, rgba(245,158,11,0.07) 0%, transparent 65%)', borderRadius: 'inherit', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: gecti ? 'linear-gradient(180deg,#fbbf24,#f59e0b40)' : 'linear-gradient(180deg,#f59e0b,#d9780620)', borderRadius: '14px 0 0 14px' }} />

            <div style={V.rekorInner}>
              {/* Sol: başlık + meta */}
              <div style={V.rekorLeft}>
                <div style={V.rekorTitleRow}>
                  <span style={{ fontSize: '1rem' }}>{gecti ? '🏆' : '🎯'}</span>
                  <span style={V.rekorTitle}>{gecti ? 'REKOR KIRILDI!' : 'Günlük Rekor Takibi'}</span>
                  {rekorTarih && (
                    <span style={{ ...V.rekorBadge, color: gecti ? '#fbbf24' : '#f59e0b', background: gecti ? 'rgba(251,191,36,0.1)' : 'rgba(245,158,11,0.08)', border: `1px solid ${gecti ? 'rgba(251,191,36,0.25)' : 'rgba(245,158,11,0.2)'}` }}>
                      {rekorTarih}
                    </span>
                  )}
                </div>
                <p style={V.rekorSub}>
                  {gecti
                    ? `Tebrikler! Bu ayın rekoru ${fmt2(rekor)} ₺ idi, şu an ${fmt2(anlikYat)} ₺ ile geçildi.`
                    : `Bu ayın en yüksek günlük yatırımını geçmek için ${fmt2(Math.max(rekor - anlikYat, 0))} ₺ daha gerekiyor.`
                  }
                </p>
              </div>

              {/* Sağ: rakamlar */}
              <div style={V.rekorRight}>
                <div style={V.rekorStat}>
                  <span style={V.rekorStatLbl}>ANLIK</span>
                  <span style={{ ...V.rekorStatVal, color: gecti ? '#fbbf24' : `hsl(${Math.round(40)}, ${Math.round(pct * 0.95)}%, ${Math.round(72 - pct * 0.1)}%)` }}>{fmt2(anlikYat)} ₺</span>
                </div>
                <div style={{ ...V.rekorDivV }} />
                <div style={V.rekorStat}>
                  <span style={V.rekorStatLbl}>REKOR</span>
                  <span style={{ ...V.rekorStatVal, color: gecti ? '#94a3b8' : '#fbbf24' }}>{fmt2(rekor)} ₺</span>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div style={V.rekorBarWrap}>
              <div style={V.rekorTrack}>
                <div style={{
                  ...V.rekorFill,
                  width: `${pct.toFixed(1)}%`,
                  background: gecti
                    ? 'linear-gradient(90deg, #fbbf24, #f59e0b, #fbbf24)'
                    : 'linear-gradient(90deg, #b45309, #d97706, #fbbf24)',
                  boxShadow: gecti ? '0 0 12px rgba(251,191,36,0.5)' : pct > 75 ? '0 0 8px rgba(251,191,36,0.3)' : 'none',
                }} />
                {/* Rekor çizgisi */}
                <div style={{ ...V.rekorLine, left: '100%' }}>
                  <div style={V.rekorLineTick} />
                </div>
              </div>
              <div style={V.rekorBarMeta}>
                <span style={{ color: gecti ? '#fbbf24' : '#f59e0b', fontSize: '0.65rem', fontWeight: 800 }}>
                  {pct.toFixed(1)}%
                </span>
                <span style={{ color: gecti ? '#fbbf24' : '#cbd5e1', fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.03em' }}>
                  {gecti ? '🏅 Yeni rekor!' : `${fmt2(rekor)} ₺ hedef`}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SEPARATOR ─────────────────────────────── */}
      <div style={V.separator}>
        <div style={V.sepLine} />
        <span style={V.sepLabel}>ANLIK KASALAR</span>
        <div style={V.sepLine} />
      </div>

      {/* ── GRID ───────────────────────────────────── */}
      {loading ? <Spinner /> : (
        <div style={V.grid}>
          {filtered.map((k, i) => {
            const oz    = ozetMap[k._id];
            const anlik = oz?.canliKasa ?? oz?.guncelKasa ?? 0;
            const liveY = oz?.liveYatirim ?? 0;
            const liveC = oz?.liveCekim   ?? 0;
            const pos   = anlik >= 0;
            const [c1, c2] = accent(k._id);
            const isHov   = hovered === k._id;
            const flash   = flashKasalar[k._id];
            const flashC  = flash ? (flash.yon === 'yatirim' ? '#4ade80' : '#f87171') : null;

            return (
              <div
                key={k._id}
                style={{
                  ...V.card,
                  animationDelay: `${i * 70}ms`,
                  transform: isHov ? 'translateY(-6px) scale(1.01)' : flash ? 'translateY(-3px) scale(1.005)' : 'translateY(0) scale(1)',
                  boxShadow: flash
                    ? `0 0 0 2px ${flashC}, 0 0 48px ${flashC}80, 0 0 80px ${flashC}30, 0 16px 48px rgba(0,0,0,0.5)`
                    : isHov
                      ? `0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px ${c1}30, inset 0 1px 0 rgba(255,255,255,0.07)`
                      : '0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
                  borderColor: flash ? `${flashC}80` : isHov ? `${c1}35` : 'rgba(255,255,255,0.055)',
                }}
                onMouseEnter={() => setHovered(k._id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* coloured corner glow */}
                <div style={{
                  position: 'absolute', top: -40, right: -40, width: 130, height: 130, borderRadius: '50%',
                  background: `radial-gradient(circle, ${c1}22 0%, transparent 70%)`,
                  pointerEvents: 'none',
                }} />

                {/* flash overlay */}
                {flash && (
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
                    background: `radial-gradient(ellipse at 50% 20%, ${flashC}45 0%, ${flashC}15 50%, transparent 75%)`,
                    animation: 'flashPulse 1.8s ease-out both',
                  }} />
                )}

                {/* top stripe */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3, borderRadius: '16px 16px 0 0',
                  background: `linear-gradient(90deg, ${c1}, ${c2})`,
                }} />

                {/* ── card header ── */}
                <div style={V.cardHead}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ ...V.kasaIcon, background: `linear-gradient(135deg, ${c1}22, ${c2}11)`, border: `1px solid ${c1}30` }}>
                      <span style={{ color: c1, fontSize: '0.75rem', fontWeight: 900 }}>{k._id.charAt(0)}</span>
                    </div>
                    <div>
                      <div style={V.kasaName}>{k._id}</div>
                      <div style={{ ...V.kasaTag, color: k.aktif ? '#22c55e' : '#475569' }}>
                        {k.aktif ? <FaToggleOn /> : <FaToggleOff />}
                        {k.aktif ? 'aktif' : 'pasif'}
                      </div>
                    </div>
                  </div>
                  <div style={V.cardActions}>
                    <button style={{ ...V.iconBtn, '--c': c1 }} onClick={() => handleOzetAc(k)} title="Özet">
                      <FaChartPie />
                    </button>
                    <button style={{ ...V.iconBtn, '--c': '#38bdf8', background: 'rgba(56,189,248,0.07)', color: '#38bdf8' }} onClick={() => handleEditAc(k)} title="Düzenle">
                      <FaEdit />
                    </button>
                  </div>
                </div>

                {/* ── divider ── */}
                <div style={V.divider} />

                {/* ── main amount ── */}
                <div style={V.amountBlock}>
                  <div style={V.amountLabel}>
                    <FaBolt style={{ color: c1, fontSize: '0.55rem' }} />
                    ANLIK KASA
                  </div>
                  {(canliLoading && ilkYukleme) ? (
                    <div style={V.skeleton} />
                  ) : (
                    <div style={{ ...V.amount, color: pos ? '#e2fbe8' : '#ffe4e4' }}>
                      <span style={{ ...V.amountNum, background: `linear-gradient(135deg, ${pos ? '#4ade80' : '#f87171'}, ${pos ? '#22c55e' : '#ef4444'})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        <AnimNum value={anlik} tick={tick} />
                      </span>
                      <span style={V.amountCur}>₺</span>
                    </div>
                  )}
                </div>

                {/* ── live chips ── */}
                {(liveY > 0 || liveC > 0) && (
                  <div style={V.chips}>
                    {liveY > 0 && (
                      <div style={V.chip('#22c55e')}>
                        <span style={{ fontSize: '0.7rem' }}>↑</span>
                        <span>{fmt(liveY)} ₺</span>
                      </div>
                    )}
                    {liveC > 0 && (
                      <div style={V.chip('#ef4444')}>
                        <span style={{ fontSize: '0.7rem' }}>↓</span>
                        <span>{fmt(liveC)} ₺</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── ÖZET MODAL ─────────────────────────────── */}
      {ozetModal && (
        <Backdrop onClose={() => setOzetModal(null)}>
          <div style={V.modal}>
            <div style={V.modalStripe(accent(ozetModal._id)[0])} />
            <div style={V.modalHead}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaChartPie style={{ color: accent(ozetModal._id)[0] }} />
                <span style={{ color: '#f1f5f9', fontWeight: 800 }}>{ozetModal._id}</span>
                <span style={{ color: '#334155', fontWeight: 400 }}>{AY_ADLARI[ay]} {yil}</span>
              </div>
              <button style={V.closeBtn} onClick={() => setOzetModal(null)}>&times;</button>
            </div>
            {ozetLoading ? <Spinner /> : ozetData && (
              <div style={V.ozetGrid}>
                {[
                  { l: 'Yatırım',  v: ozetData.gunluk?.toplamYatirim,    c: '#22c55e' },
                  { l: 'Çekim',    v: ozetData.gunluk?.toplamCekim,      c: '#ef4444' },
                  { l: 'Komisyon', v: ozetData.gunluk?.toplamKomisyon,   c: '#f59e0b' },
                  { l: 'Ödeme',    v: ozetData.odeme?.toplamOdeme,       c: '#a78bfa' },
                  { l: 'Tahsilat', v: ozetData.tahsilat?.toplamTahsilat, c: '#38bdf8' },
                ].map(({ l, v, c }) => (
                  <div key={l} style={{ background: `${c}09`, border: `1px solid ${c}20`, borderRadius: 14, padding: '14px 16px' }}>
                    <div style={{ color: '#334155', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{l}</div>
                    <div style={{ color: c, fontSize: '1rem', fontWeight: 800 }}>{fmt(v)} ₺</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Backdrop>
      )}

      {/* ── EDIT MODAL ─────────────────────────────── */}
      {editModal && (
        <Backdrop onClose={() => setEditModal(null)}>
          <div style={{ ...V.modal, maxWidth: 380 }}>
            <div style={V.modalStripe(accent(editModal._id)[0])} />
            <div style={V.modalHead}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaEdit style={{ color: accent(editModal._id)[0] }} />
                <span style={{ color: '#f1f5f9', fontWeight: 800 }}>{editModal._id}</span>
                <span style={{ color: '#334155', fontWeight: 400 }}>güncelle</span>
              </div>
              <button style={V.closeBtn} onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[['Devir Kasası (TL)', 'devirKasasi'], ['Döviz Kuru', 'dovizKuru']].map(([lbl, key]) => (
                <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={V.fieldLbl}>{lbl}</span>
                  <input style={V.input} type="number" value={form[key]}
                    onChange={e => setForm({ ...form, [key]: parseFloat(e.target.value) })} />
                </label>
              ))}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={V.fieldLbl}>Durum</span>
                <select style={V.input} value={form.aktif ? 'true' : 'false'}
                  onChange={e => setForm({ ...form, aktif: e.target.value === 'true' })}>
                  <option value="true">Aktif</option>
                  <option value="false">Pasif</option>
                </select>
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button style={V.btnCancel} onClick={() => setEditModal(null)}>İptal</button>
                <button style={V.btnSave} onClick={handleGuncelle}>Kaydet</button>
              </div>
            </div>
          </div>
        </Backdrop>
      )}

      <KasaNotifOverlay />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800;900&family=DM+Sans:wght@400;500;700&display=swap');
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:translateY(0); } }
        @keyframes blink  { 0%,100%{ opacity:1; } 50%{ opacity:.3; } }
        @keyframes notifSlideDown { from { opacity:0; transform: translateY(-100%); } to { opacity:1; transform: translateY(0); } }
        @keyframes notifProgress { from { width: 100%; } to { width: 0%; } }
        @keyframes flashPulse { 0% { opacity:1; } 40% { opacity:0.9; } 100% { opacity:0; } }
        * { transition: transform 0.25s cubic-bezier(.22,1,.36,1), box-shadow 0.25s ease, border-color 0.25s ease; }
        select option { background: #0d0f13; }
        input::placeholder, textarea::placeholder { color:#1e293b; }
      `}</style>
    </div>
  );
};

/* ─── helpers ─────────────────────────────────────── */
const Backdrop = ({ children, onClose }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.87)', backdropFilter:'blur(12px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()}>{children}</div>
  </div>
);

const Spinner = () => (
  <div style={{ display:'flex', justifyContent:'center', padding:60 }}>
    <div style={{ width:30, height:30, borderRadius:'50%', border:'2.5px solid #1a1d22', borderTopColor:'#f27127', animation:'spin .7s linear infinite' }} />
  </div>
);

/* ─── styles ──────────────────────────────────────── */
const V = {
  page: {
    padding: '28px 26px',
    minHeight: '100vh',
    backgroundColor: '#08090c',
    fontFamily: "'DM Sans', sans-serif",
    color: '#e2e8f0',
    position: 'relative',
    overflow: 'hidden',
  },
  blob: { position: 'fixed', width: 500, height: 500, pointerEvents: 'none', zIndex: 0 },

  /* header */
  header: { position: 'relative', zIndex: 1, marginBottom: 32 },
  headerTop: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 },
  eyebrow: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 },
  statusDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: 6 },
  title: { fontFamily: "'Syne', sans-serif", fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 900, color: '#f1f5f9', margin: 0, letterSpacing: '-0.02em', lineHeight: 1 },

  controls: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: 8, background: '#0f1012', border: '1.5px solid #2d3238', borderRadius: 10, padding: '8px 13px' },
  searchInput: { background: 'none', border: 'none', outline: 'none', color: '#e2e8f0', fontSize: '0.82rem', width: 110 },
  sel: { background: '#0f1012', border: '1.5px solid #2d3238', borderRadius: 10, color: '#e2e8f0', padding: '9px 14px', fontSize: '0.85rem', cursor: 'pointer' },
  syncBtn: { background: 'rgba(242,113,39,0.1)', border: '1px solid rgba(242,113,39,0.2)', color: '#f27127', borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.9rem' },

  /* grid */
  grid: {
    position: 'relative', zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 18,
  },

  card: {
    position: 'relative', borderRadius: 18, overflow: 'hidden',
    padding: '22px 22px 20px',
    background: 'linear-gradient(160deg, rgba(22,24,30,0.95) 0%, rgba(14,16,20,0.98) 100%)',
    border: '1px solid',
    backdropFilter: 'blur(16px)',
    display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center',
    animation: 'fadeUp 0.5s cubic-bezier(.22,1,.36,1) both',
    cursor: 'default',
    willChange: 'transform',
  },

  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' },
  kasaIcon: { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kasaName: { fontFamily: "'Syne', sans-serif", fontSize: '0.98rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '0.02em' },
  kasaTag: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', marginTop: 3 },
  cardActions: { display: 'flex', gap: 6 },
  iconBtn: { width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', background: 'rgba(242,113,39,0.08)', color: '#f27127' },

  divider: { height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)', width: '100%' },

  amountBlock: { textAlign: 'center' },
  amountLabel: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.14em', color: '#475569', marginBottom: 8 },
  amount: { display: 'flex', alignItems: 'baseline', gap: 5 },
  amountNum: { fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1 },
  amountCur: { fontSize: '1rem', fontWeight: 700, color: '#475569' },
  skeleton: { height: 36, width: '70%', borderRadius: 8, background: 'linear-gradient(90deg, #1a1d22, #22262e, #1a1d22)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s ease infinite' },

  chips: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, flexWrap: 'wrap' },
  chip: c => ({ display: 'inline-flex', alignItems: 'center', gap: 4, background: `${c}10`, border: `1px solid ${c}22`, borderRadius: 20, padding: '3px 10px', color: c, fontSize: '0.72rem', fontWeight: 700 }),
  chipLabel: { color: '#1e293b', fontSize: '0.6rem', letterSpacing: '0.06em' },

  /* modal */
  modal: {
    position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(160deg, #10131a, #0c0e14)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20, padding: '26px 26px 22px',
    width: '90vw', maxWidth: 540,
    boxShadow: '0 40px 80px rgba(0,0,0,0.7)',
    animation: 'fadeUp 0.25s ease',
  },
  modalStripe: c => ({ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${c}, transparent)` }),
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, fontSize: '0.95rem' },
  closeBtn: { background: 'none', border: 'none', color: '#334155', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 },
  ozetGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 },

  fieldLbl: { color: '#475569', fontSize: '0.67rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' },
  input: { background: '#0a0c10', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, color: '#e2e8f0', padding: '10px 14px', fontSize: '0.85rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  btnCancel: { padding: '9px 18px', borderRadius: 10, background: '#14171f', color: '#64748b', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.8rem' },
  btnSave:   { padding: '9px 22px', borderRadius: 10, background: 'linear-gradient(135deg,#f27127,#ff9a44)', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.8rem' },

  /* anlık özet kartları */
  ozKartGrid: {
    position: 'relative', zIndex: 1,
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14, marginBottom: 22,
  },
  ozKart: {
    borderRadius: 14, overflow: 'hidden', position: 'relative',
    background: 'rgba(13,15,18,0.9)',
    border: '1px solid rgba(255,255,255,0.05)',
    animation: 'fadeUp 0.45s cubic-bezier(.22,1,.36,1) both',
    boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
  },
  ozKartInner: { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px 14px 20px', justifyContent: 'center' },
  ozKartSymBox: { width: 42, height: 42, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  ozKartContent: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  ozKartLabel: { fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', display: 'block', marginBottom: 5, textAlign: 'center' },
  ozKartVal: { fontSize: '1.3rem', fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 3, marginBottom: 9 },
  ozKartTL: { fontSize: '0.78rem', fontWeight: 600, opacity: 0.45 },
  ozKartTrack: { height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.05)', overflow: 'hidden', width: '100%' },
  ozKartFill: { height: '100%', borderRadius: 2, transition: 'width 1s cubic-bezier(.22,1,.36,1)' },
  ozKartSkel: { display: 'block', height: 24, width: '75%', borderRadius: 6, background: 'linear-gradient(90deg,#1a1d22,#22262e,#1a1d22)', backgroundSize: '200%', animation: 'shimmer 1.4s ease infinite' },

  /* separator */
  separator: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, position: 'relative', zIndex: 1 },
  sepLine: { flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)' },
  sepLabel: { color: '#1e293b', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', whiteSpace: 'nowrap' },

  /* rekor challenge */
  rekorCard: {
    position: 'relative', zIndex: 1, overflow: 'hidden',
    background: 'rgba(13,15,18,0.95)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 14, marginBottom: 20,
    boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
    animation: 'fadeUp 0.5s ease both',
  },
  rekorInner: { display: 'flex', alignItems: 'center', gap: 20, padding: '18px 22px 14px 22px', flexWrap: 'wrap' },
  rekorLeft: { flex: 1, minWidth: 260 },
  rekorTitleRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, flexWrap: 'wrap' },
  rekorTitle: { color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.01em' },
  rekorBadge: { fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '2px 8px', borderRadius: 5 },
  rekorSub: { color: '#64748b', fontSize: '0.72rem', lineHeight: 1.5, margin: 0 },
  rekorRight: { display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 },
  rekorStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '0 20px' },
  rekorStatLbl: { color: '#64748b', fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' },
  rekorStatVal: { fontSize: '1.1rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' },
  rekorDivV: { width: 1, height: 36, background: 'rgba(255,255,255,0.05)' },
  rekorBarWrap: { padding: '0 22px 16px' },
  rekorTrack: { position: 'relative', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'visible', marginBottom: 6 },
  rekorFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 3, transition: 'width 1.2s cubic-bezier(.22,1,.36,1), background 0.5s ease' },
  rekorLine: { position: 'absolute', top: -3, transform: 'translateX(-1px)', pointerEvents: 'none' },
  rekorLineTick: { width: 2, height: 12, background: 'rgba(255,255,255,0.15)', borderRadius: 1 },
  rekorBarMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
};

export default KasaYonetimi;
